import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import styles from "./FinancialTab.module.scss";
import classNames from "classnames/bind";
import FinancialCombinedContent from "./component/main-content/FinancialCombinedContent";
import CreateInvoice from "./component/create-invoice/CreateInvoice";
import {
  ICreditMemoJobDetails,
  IInvoiceJobDetails,
  InvoiceDetailsAction,
  InvoiceFormSource,
} from "~/utils/interface/jobDetails";
import ViewDetails from "./component/view-details/ViewDetails";
import CreateNewBudget, {
  CreateNewBudgetRef,
} from "./component/create-new-budget/CreateNewBudget";
import ViewDetailsBudget from "./component/view-details-budget/ViewDetailsBudget";
import CreateNewCreditMemo from "./component/create-new-credit-memo/CreateNewCreditMemo";

const cx = classNames.bind(styles);

interface FinancialTabProps {
  jobId: string;
  onAddNote?: () => void;
  initialSubTab?: "invoice" | "payment" | "budget-tracker" | "credit-memo";
  onDetailsOpenChange?: (isOpen: boolean) => void;
  onRegisterBackFromDetails?: (back: (() => void) | null) => void;
}

export interface FinancialTabRef {
  requestLeaveFromCreateBudget: (onLeave: () => void) => void;
}

const FinancialTab = forwardRef<FinancialTabRef, FinancialTabProps>(
  function FinancialTab({ jobId, onAddNote, initialSubTab, onDetailsOpenChange, onRegisterBackFromDetails }, ref) {
  const createBudgetRef = useRef<CreateNewBudgetRef>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const budgetIdFromUrl = searchParams.get("budget") ?? null;
  // ?invoice=<id> opens that invoice's detail (used as the Stripe checkout return URL)
  const invoiceIdFromUrl = searchParams.get("invoice");

  const [activeTab, setActiveTab] = useState(invoiceIdFromUrl ? "view-details" : "main-content");
  const [getListInvoice, setGetListInvoice] = useState(false);
  const [listInvoice, setListInvoice] = useState<IInvoiceJobDetails[]>([]);
  const [invoiceId, setInvoiceId] = useState<string>(invoiceIdFromUrl ?? "");
  const [invoiceFormSource, setInvoiceFormSource] = useState<InvoiceFormSource | undefined>();
  const [invoiceDetailsAction, setInvoiceDetailsAction] = useState<InvoiceDetailsAction | undefined>();
  const [getListPayment, setGetListPayment] = useState(false);
  const [getListBudgetTracker, setGetListBudgetTracker] = useState(false);
  const [getListCreditMemo, setGetListCreditMemo] = useState(false);
  const [defaultSubTab, setDefaultSubTab] = useState<
    "invoice" | "payment" | "budget-tracker" | "credit-memo" | undefined
  >(initialSubTab);
  const [budgetEditId, setBudgetEditId] = useState<string | undefined>(
    undefined
  );
  const [isEditBudget, setIsEditBudget] = useState<boolean>(false);
  const [budgetId, setBudgetId] = useState<string>("");
  const [listCreditMemo, setListCreditMemo] = useState<ICreditMemoJobDetails[]>(
    []
  );
  const [creditMemoEditId, setCreditMemoEditId] = useState<string | undefined>(
    undefined
  );
  const [isEditCreditMemo, setIsEditCreditMemo] = useState<boolean>(false);

  useImperativeHandle(
    ref,
    () => ({
      requestLeaveFromCreateBudget: (onLeave: () => void) => {
        if (createBudgetRef.current) {
          createBudgetRef.current.requestLeave(onLeave);
        } else {
          onLeave();
        }
      },
    }),
    []
  );

  useEffect(() => {
    if (!invoiceIdFromUrl) return;
    const next = new URLSearchParams(searchParams);
    next.delete("invoice");
    setSearchParams(next, { replace: true });
  }, [invoiceIdFromUrl, searchParams, setSearchParams]);

  useEffect(() => {
    if (!budgetIdFromUrl && activeTab === "view-details-budget") {
      setDefaultSubTab("budget-tracker");
      setActiveTab("main-content");
    }
  }, [budgetIdFromUrl, activeTab]);

  const handleCreateInvoice = (listInvoice: IInvoiceJobDetails[], source?: InvoiceFormSource) => {
    setInvoiceFormSource(source);
    setActiveTab("create-invoice");
    setListInvoice(listInvoice);
  };

  const handleCancelCreateInvoice = () => {
    setDefaultSubTab("invoice");
    setActiveTab("main-content");
  };

  // FB-27: after saving, land on the invoice itself; a new invoice opens its customer preview.
  const handleSaveInvoice = (savedInvoiceId?: string) => {
    setGetListInvoice(true);
    if (!savedInvoiceId) {
      setDefaultSubTab("invoice");
      setActiveTab("main-content");
      return;
    }
    handleViewDetails(savedInvoiceId, invoiceFormSource?.mode === "edit" ? undefined : "preview");
  };

  const handleViewDetails = (invoiceId: string, action?: InvoiceDetailsAction) => {
    setInvoiceId(invoiceId);
    setInvoiceDetailsAction(action);
    setActiveTab("view-details");
  };

  const handleInvoiceDeleted = () => {
    setGetListInvoice(true);
    setDefaultSubTab("invoice");
    setActiveTab("main-content");
  };

  const handleCreateBudgetTracker = (idBudget?: string, isEdit?: boolean) => {
    setBudgetEditId(idBudget);
    setIsEditBudget(isEdit || false);
    setActiveTab("create-budget-tracker");
  };

  const handleCancelCreateBudgetTracker = () => {
    setDefaultSubTab("budget-tracker");
    setActiveTab("main-content");
    setBudgetEditId(undefined);
    setIsEditBudget(false);
  };

  const handleSaveBudgetTracker = () => {
    setDefaultSubTab("budget-tracker");
    setActiveTab("main-content");
    setGetListBudgetTracker(true);
    setBudgetEditId(undefined);
    setIsEditBudget(false);
  };

  const handleViewDetailsBudget = (idBudget: string) => {
    setBudgetId(idBudget);
    setActiveTab("view-details-budget");
    const next = new URLSearchParams();
    next.set("financials", "");
    next.set("subtab", "budget-tracker");
    next.set("budget", idBudget);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: false });
  };

  const handleCreateCreditMemo = (listCreditMemo: ICreditMemoJobDetails[]) => {
    setActiveTab("create-credit-memo");
    setListCreditMemo(listCreditMemo);
  };

  const handleCancelCreateCreditMemo = () => {
    setDefaultSubTab("credit-memo");
    setActiveTab("main-content");
    setCreditMemoEditId(undefined);
  };

  const handleSaveCreditMemo = () => {
    setDefaultSubTab("credit-memo");
    setActiveTab("main-content");
    setGetListCreditMemo(true);
    setCreditMemoEditId(undefined);
  };

  const handleDefaultSubTabApplied = useCallback(() => {
    setDefaultSubTab(undefined);
  }, []);

  const handleEditCreditMemo = (idCreditMemo: string) => {
    setCreditMemoEditId(idCreditMemo);
    setIsEditCreditMemo(true);
    setActiveTab("create-credit-memo");
  };

  const handleBackFromViewDetailsBudget = () => {
    setDefaultSubTab("budget-tracker");
    setActiveTab("main-content");
    const next = new URLSearchParams();
    next.set("financials", "");
    next.set("subtab", "budget-tracker");
    setSearchParams(next, { replace: true });
  };

  const isDetailsOpen =
    activeTab === "view-details" ||
    activeTab === "view-details-budget" ||
    !!budgetIdFromUrl;

  useEffect(() => {
    onDetailsOpenChange?.(isDetailsOpen);
  }, [isDetailsOpen, onDetailsOpenChange]);

  useEffect(() => {
    if (isDetailsOpen) {
      onRegisterBackFromDetails?.(() => {
        if (activeTab === "view-details") {
          setActiveTab("main-content");
        } else {
          handleBackFromViewDetailsBudget();
        }
      });
    } else {
      onRegisterBackFromDetails?.(null);
    }
    return () => onRegisterBackFromDetails?.(null);
  }, [isDetailsOpen, activeTab, onRegisterBackFromDetails]);

  const renderTabContent = () => {
    if (budgetIdFromUrl) {
      return (
        <ViewDetailsBudget
          jobId={jobId}
          idBudget={budgetIdFromUrl}
          onAddNote={onAddNote}
          onBack={handleBackFromViewDetailsBudget}
        />
      );
    }

    switch (activeTab) {
      case "main-content":
        return (
          <FinancialCombinedContent
            jobId={jobId}
            handleCreateInvoice={handleCreateInvoice}
            getListInvoice={getListInvoice}
            setGetListInvoice={setGetListInvoice}
            handleViewDetails={handleViewDetails}
            getListPayment={getListPayment}
            setGetListPayment={setGetListPayment}
            getListBudgetTracker={getListBudgetTracker}
            setGetListBudgetTracker={setGetListBudgetTracker}
            getListCreditMemo={getListCreditMemo}
            setGetListCreditMemo={setGetListCreditMemo}
            handleCreateBudgetTracker={handleCreateBudgetTracker}
            handleViewDetailsBudget={handleViewDetailsBudget}
            handleCreateCreditMemo={handleCreateCreditMemo}
            defaultSubTab={defaultSubTab}
            onDefaultSubTabApplied={handleDefaultSubTabApplied}
            onAddNote={onAddNote}
            handleEditCreditMemo={handleEditCreditMemo}
          />
        );
      case "create-invoice":
        return (
          <CreateInvoice
            jobId={jobId}
            onSaveInvoice={handleSaveInvoice}
            onCancel={handleCancelCreateInvoice}
            listInvoice={listInvoice}
            source={invoiceFormSource}
          />
        );
      case "view-details":
        return (
          <ViewDetails
            jobId={jobId}
            invoiceId={invoiceId}
            onAddNote={onAddNote}
            initialAction={invoiceDetailsAction}
            onEdit={(id) => handleCreateInvoice(listInvoice, { mode: "edit", invoiceId: id })}
            onDeleted={handleInvoiceDeleted}
          />
        );
      case "create-budget-tracker":
        return (
          <CreateNewBudget
            ref={createBudgetRef}
            jobId={jobId}
            onCancel={handleCancelCreateBudgetTracker}
            onSave={handleSaveBudgetTracker}
            idBudget={budgetEditId}
            isEdit={isEditBudget}
          />
        );
      case "view-details-budget":
        return (
          <ViewDetailsBudget
            jobId={jobId}
            idBudget={budgetId}
            onAddNote={onAddNote}
            onBack={handleBackFromViewDetailsBudget}
          />
        );
      case "create-credit-memo":
        return (
          <CreateNewCreditMemo
            jobId={jobId}
            onCancel={handleCancelCreateCreditMemo}
            onSaveCreditMemo={handleSaveCreditMemo}
            listCreditMemo={listCreditMemo}
            idCreditMemo={creditMemoEditId}
            isEdit={isEditCreditMemo}
          />
        );
      default:
        return (
          <FinancialCombinedContent
            jobId={jobId}
            handleCreateInvoice={handleCreateInvoice}
            getListInvoice={getListInvoice}
            setGetListInvoice={setGetListInvoice}
            handleViewDetails={handleViewDetails}
            getListPayment={getListPayment}
            setGetListPayment={setGetListPayment}
            getListBudgetTracker={getListBudgetTracker}
            setGetListBudgetTracker={setGetListBudgetTracker}
            getListCreditMemo={getListCreditMemo}
            setGetListCreditMemo={setGetListCreditMemo}
            handleCreateBudgetTracker={handleCreateBudgetTracker}
            handleViewDetailsBudget={handleViewDetailsBudget}
            handleCreateCreditMemo={handleCreateCreditMemo}
            defaultSubTab={defaultSubTab}
            onDefaultSubTabApplied={handleDefaultSubTabApplied}
            onAddNote={onAddNote}
            handleEditCreditMemo={handleEditCreditMemo}
          />
        );
    }
  };
  return (
    <div className={cx("financial-tab-container")}>{renderTabContent()}</div>
  );
});

export default FinancialTab;
